import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { defaultTo } from 'lodash-es';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, switchMap, takeUntil } from 'rxjs/operators';

import { TmpI18NService } from 'app/i18n/tmp-i18n.service';
import { ListsService } from 'app/lists/lists.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { TeamService } from 'app/shared/service/team.service';
import { ToastrService } from 'app/shared/service/toastr.service';
import { UserService } from 'app/user/user.service';

@Component({
  selector: 'cc-list-project-unsubscription',
  templateUrl: './list-project-unsubscription.component.html',
  styleUrls: ['./list-project-unsubscription.component.scss'],
})
export class ListProjectUnsubscriptionComponent implements OnDestroy, OnInit {
  private readonly destroy$ = new Subject<void>();
  projectId: string;
  listId: string;
  propertyId: string;
  userId: string;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly errorHandler: ErrorHandler,
    private readonly i18nService: TmpI18NService,
    private readonly listsService: ListsService,
    private readonly router: Router,
    private readonly teamService: TeamService,
    private readonly toastrService: ToastrService,
    private readonly userService: UserService,
  ) {}

  ngOnInit() {
    this.activatedRoute.params.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(
      (params) => {
        this.projectId = params.projectId;
      },
      (error) => {
        this.errorHandler.handle(error);
      },
    );

    this.teamService.currentTeam
      .pipe(
        filter((project) => project.id && project.isProject),
        takeUntil(this.destroy$),
        switchMap(() => this.activatedRoute.params),
        switchMap((params) => {
          this.propertyId = params.propertyId;
          this.listId = params.listId;
          this.projectId = params.projectId;
          return this.userService.currentUser;
        }),
        filter((user) => !!user),
        switchMap((user) => {
          this.userId = user.id;
          return this.listsService.getList(this.listId).pipe(takeUntil(this.destroy$));
        }),
        switchMap((list) => {
          if (list.parents[0] !== this.projectId) {
            throw this.i18nService.getMessage('listUnsubscribeProjectMismatch');
          }
          list.properties
            .filter((property) => property.id === this.propertyId)
            .forEach((property) => {
              const subscribedUsers = defaultTo(
                property.subscribedUsers,
                {},
              ) as typeof property.subscribedUsers;
              const userIds = defaultTo(subscribedUsers.userIds, []) as typeof subscribedUsers.userIds;
              subscribedUsers.userIds = userIds.filter((userId) => userId !== this.userId);
              property.subscribedUsers = subscribedUsers;
            });
          return this.listsService.updateProjectList(this.projectId, this.listId, list);
        }),
      )
      .subscribe(
        () => {
          this.toastrService.successByKey('listUnsubscribeSuccess');
          void this.router.navigate(['/', 'lists', 'projects', this.projectId, this.listId]);
        },
        (error) => {
          this.errorHandler.handle(error);
          void this.router.navigate(['/', 'lists', 'projects', this.projectId, this.listId]);
        },
      );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
